import styled from '@emotion/styled'
import { Box, Typography } from '@mui/material'
import { IIRM } from 'interfaces/irm.interface'
import { useMemo } from 'react'
import {
  AreaChart,
  Tooltip,
  Area,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { theme } from 'theme'
import { toA } from 'utils/display'
import simulateIRM, { IRMKind } from 'utils/simulateIRM'

const supplyColor = 'rgba(227, 97, 57)'
const borrowColor = 'rgba(227, 190, 57)'

const TooltipContainer = styled(Box)`
  background: #ffffff;
  border-radius: 4px;
  padding: 10px 12px;
  filter: drop-shadow(0px 0px 10px rgba(18, 18, 18, 0.12));
`

const CustomTooltip = (props: any) => {
  const { payload: _payload, label } = props
  const payload =
    _payload.length !== 0 ? (_payload[0] as PayloadProps) : undefined

  const payloads: PayloadDisplay[] = [
    {
      fill: borrowColor,
      title: 'Borrow APR',
      value: payload?.payload.borrowAPR || 0,
    },
    {
      fill: supplyColor,
      title: 'Supply APR',
      value: payload?.payload.supplyAPR || 0,
    },
  ]

  return (
    <TooltipContainer>
      <Typography variant="body1" fontSize={12} fontWeight={600} mb="4px">
        Utilization {label}%
      </Typography>
      {payloads.map((item, i) => (
        <Box display="flex" alignItems="center" gap="4px" mb="4px" key={i}>
          <Box
            sx={{
              height: '12px',
              width: '12px',
              backgroundColor: item.fill,
              borderRadius: '100%',
            }}
          />
          <Typography variant="caption">{item.title}</Typography>
          <Typography variant="caption" fontWeight={600} color="text.primary">
            {toA(item.value)}%
          </Typography>
        </Box>
      ))}
    </TooltipContainer>
  )
}

function ReferenceLabel(props: any) {
  const { value, textAnchor, viewBox, dy, dx, flipped } = props

  const x = viewBox.width + viewBox.x + (flipped ? -75 : 5)
  const y = viewBox.y - 10

  return (
    <text
      x={x}
      y={y}
      dy={dy}
      dx={dx}
      fill={theme.palette.text.secondary}
      fontSize={12}
      textAnchor={textAnchor}
    >
      {value}
    </text>
  )
}

interface Props {
  adminFee: number
  currentUtilization?: number
  IRM: IRMKind
  irmInfo: IIRM
}
const IRMChart = ({ adminFee, currentUtilization, IRM, irmInfo }: Props) => {
  const data = useMemo(
    () => simulateIRM[IRM](adminFee, irmInfo as any),
    [IRM, adminFee, irmInfo],
  )

  return (
    <ResponsiveContainer>
      <AreaChart data={data} margin={{ top: 30, right: 0, left: 0, bottom: 0 }}>
        <Tooltip
          content={
            <CustomTooltip
              key={Math.random()}
              defaultValue={currentUtilization}
            />
          }
        />
        <ReferenceLine
          x={Math.ceil(currentUtilization || 0)}
          stroke={theme.palette.text.primary}
          label={
            <ReferenceLabel
              value={`Current ${currentUtilization}%`}
              flipped={(currentUtilization || 0) > 50}
            />
          }
          strokeDasharray={4}
        />
        <Area
          type="monotone"
          dataKey="borrowAPR"
          fillOpacity={1}
          stroke={borrowColor}
          fill={borrowColor}
          opacity={0.48}
          activeDot={{ r: 6 }}
        />
        <Area
          type="monotone"
          dataKey="supplyAPR"
          fillOpacity={1}
          stroke={supplyColor}
          fill={supplyColor}
          opacity={0.48}
          activeDot={{ r: 6 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
export default IRMChart

interface PayloadProps {
  color: string
  dataKey: string
  fill: string
  fillOpacity: number
  name: string
  opacity: number
  payload: {
    borrowAPR: number
    supplyAPR: number
    utilization: string
  }
  stroke: string
  value: number
}

interface PayloadDisplay {
  fill: string
  title: string
  value: number
}
